import React from 'react';
import PropTypes from 'prop-types';

import {translate} from 'views/module';

import FormQuestion from '../components/FormQuestion';

const ExperienceFormComponent = (props) => {
  const {onSubmit, isSubmitting, onProgressChange, showPopper, isFinalForm} = props;

  const [pageIndex, setPageIndex] = React.useState(0);
  const [answersByQuestion, setAnswersByQuestion] = React.useState({});

  const questions = React.useMemo(() => ([
    {
      questionId: 'experienceYears',
      fieldType: 'radio',
      rank: 1,
      questionData: {
        label: translate('form.IntakeForms.Experience.Questions.ExperienceYears.Label'),
        helpText: '',
        required: true,
      },
      options: [
        {value: '1-2-years', label: translate('form.IntakeForms.Experience.Questions.ExperienceYears.Options.OneTwo'), isOther: false},
        {value: '2-5-years', label: translate('form.IntakeForms.Experience.Questions.ExperienceYears.Options.TwoFive'), isOther: false},
        {value: 'more-5-years', label: translate('form.IntakeForms.Experience.Questions.ExperienceYears.Options.MoreFive'), isOther: false},
        {value: 'no-exp', label: translate('form.IntakeForms.Experience.Questions.ExperienceYears.Options.NoExp'), isOther: false},
      ],
    },
    {
      questionId: 'careSettings',
      fieldType: 'checkbox',
      rank: 2,
      questionData: {
        label: translate('form.IntakeForms.Experience.Questions.CareSettings.Label'),
        helpText: '',
        required: false,
      },
      options: [
        {value: 'private', label: translate('form.IntakeForms.Experience.Questions.CareSettings.Options.Private'), isOther: false},
        {value: 'retirement', label: translate('form.IntakeForms.Experience.Questions.CareSettings.Options.Retirement'), isOther: false},
        {value: 'public', label: translate('form.IntakeForms.Experience.Questions.CareSettings.Options.Public'), isOther: false},
        {value: 'community-programs', label: translate('form.IntakeForms.Experience.Questions.CareSettings.Options.CommunityPrograms'), isOther: false},
        {value: 'hospitals', label: translate('form.IntakeForms.Experience.Questions.CareSettings.Options.Hospitals'), isOther: false},
        {value: 'other', label: translate('form.IntakeForms.Experience.Questions.CareSettings.Options.Other'), isOther: true},
      ],
    },
    {
      questionId: 'accreditations',
      fieldType: 'checkbox',
      rank: 3,
      questionData: {
        label: translate('form.IntakeForms.Experience.Questions.Accreditations.Label'),
        helpText: '',
        required: false,
      },
      options: [
        {value: 'psw', label: translate('form.IntakeForms.Experience.Questions.Accreditations.Options.PSW'), isOther: false},
        {value: 'dsw', label: translate('form.IntakeForms.Experience.Questions.Accreditations.Options.DSW'), isOther: false},
        {value: 'csw', label: translate('form.IntakeForms.Experience.Questions.Accreditations.Options.CSW'), isOther: false},
        {value: 'hca', label: translate('form.IntakeForms.Experience.Questions.Accreditations.Options.HCA'), isOther: false},
        {value: 'nursing', label: translate('form.IntakeForms.Experience.Questions.Accreditations.Options.Nursing'), isOther: false},
        {value: 'nursingStudent', label: translate('form.IntakeForms.Experience.Questions.Accreditations.Options.NursingStudent'), isOther: false},
        {value: 'other', label: translate('form.IntakeForms.Experience.Questions.Accreditations.Options.Other'), isOther: true},
      ],
    },
    {
      questionId: 'cprFirstAid',
      fieldType: 'radio',
      rank: 4,
      questionData: {
        label: translate('form.IntakeForms.Experience.Questions.CPRFirstAid.Label'),
        helpText: '',
        required: true,
      },
      options: [
        {value: true, label: translate('form.IntakeForms.Experience.Questions.CPRFirstAid.Options.Yes'), isOther: false},
        {value: false, label: translate('form.IntakeForms.Experience.Questions.CPRFirstAid.Options.No'), isOther: false},
      ],
    },
    {
      questionId: 'careTasks',
      fieldType: 'checkbox',
      rank: 5,
      questionData: {
        label: translate('form.IntakeForms.Experience.Questions.CareTasks.Label'),
        helpText: '',
        required: false,
      },
      options: [
        {value: 'memory-care', label: translate('form.IntakeForms.Experience.Questions.CareTasks.Options.MemoryCare'), isOther: false},
        {value: 'mobility-support', label: translate('form.IntakeForms.Experience.Questions.CareTasks.Options.MobilitySupport'), isOther: false},
        {value: 'personal-care', label: translate('form.IntakeForms.Experience.Questions.CareTasks.Options.PersonalCare'), isOther: false},
        {value: 'medication-reminders', label: translate('form.IntakeForms.Experience.Questions.CareTasks.Options.MedicationReminders'), isOther: false},
        {value: 'meal-preparation', label: translate('form.IntakeForms.Experience.Questions.CareTasks.Options.MealPreparation'), isOther: false},
        {value: 'light-housekeeping', label: translate('form.IntakeForms.Experience.Questions.CareTasks.Options.LightHousekeeping'), isOther: false},
        {value: 'other', label: translate('form.IntakeForms.Experience.Questions.CareTasks.Options.Other'), isOther: true},
      ],
    },
  ]), [translate]);

  React.useLayoutEffect(() => {
    onProgressChange?.({current: pageIndex + 1, total: questions.length});
  }, [pageIndex, questions.length, onProgressChange]);

  const currentQuestion = questions[pageIndex];
  const currentAnswer = answersByQuestion[currentQuestion?.questionId] || null;
  const shouldSubmit = pageIndex >= questions.length - 1;
  const isFinalSubmit = Boolean(isFinalForm && shouldSubmit);

  const handleNext = async (answer) => {
    if (!currentQuestion) return;
    setAnswersByQuestion((prev) => ({
      ...prev,
      [currentQuestion.questionId]: answer,
    }));
    if (shouldSubmit) {
      const nextAnswers = {...answersByQuestion, [currentQuestion.questionId]: answer};
      const bioQuestions = Object.values(nextAnswers).filter((entry) => {
        const value = entry.questionData.value;
        if (typeof value === 'object') {
          const selected = value.selected;
          const otherText = value.otherText;
          return selected.length > 0 || Boolean(otherText.trim());
        }
        if (value.length !== undefined) {
          return value.length > 0;
        }
        if (value === 'other') {
          return false;
        }
        return value !== '';
      });
      onSubmit({bioQuestions});
      return;
    }
    setPageIndex((idx) => idx + 1);
  };

  const handlePrevious = () => {
    if (pageIndex > 0) {
      setPageIndex((idx) => idx - 1);
    }
  };

  const displayQuestion = React.useMemo(() => ({
    ...currentQuestion,
    rank: pageIndex + 1,
  }), [currentQuestion, pageIndex]);

  return (
    <FormQuestion
      question={displayQuestion}
      buttonLabel={isFinalSubmit ? translate('form.FormStep.Submit') : translate('form.FormStep.Next')}
      onNext={handleNext}
      onPrevious={handlePrevious}
      answer={currentAnswer}
      isLoading={isSubmitting}
      isSubmit={isFinalSubmit}
      showPopper={showPopper}
    />
  );
};

ExperienceFormComponent.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  onProgressChange: PropTypes.func,
  showPopper: PropTypes.bool,
  isFinalForm: PropTypes.bool,
};

export const ExperienceForm = {
  title: translate('form.IntakeForms.Experience.Title'),
  formVersion: 1,
  description: translate('form.IntakeForms.Experience.Description'),
  component: ExperienceFormComponent,
};

export default React.memo(ExperienceFormComponent);
